import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { SortableHeader, nextSort, type SortState } from '../ui/SortableHeader';
import { ColumnFilter } from '../ui/ColumnFilter';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { useColumnFilters } from '../../hooks/useColumnFilters';
import type { AbsenceCoarseGroup, AbsenceFineGroup, AbsenceRow, AbsenceSummary, HoursViewer } from '../../types/timerocket';
import { formatDateLong, formatDays, formatMinutesAsDays, formatPercent, minutesAsDays } from './format';
import { DiscrepancyBadges } from './DiscrepancyBadges';

/**
 * One row per person for the period: target days, the absence buckets as DAYS, the two rates.
 * Table standard: sticky sortable headers, drag-resizable columns, Excel-style column filters,
 * sums over the FILTERED rows.
 *
 * Two column sets, decided by the response's `typesVisible` — never by the client:
 *   - the COARSE buckets (Feiertag · Ferien · Absenz · Auswärts) for every scope — the headers
 *     are the tenant's own group names, shown verbatim like the account labels in HoursTable;
 *   - the FINE groups (Sickness · Accident · Other health · Compensation · Other absence) and
 *     the sick rate only when the server delivered them (own scope, or page CanEdit). These
 *     headers are ours (the groups are HR's classification, not a TimeRocket label), so they
 *     are English like the rest of the UI. For a supervisor's team the fine columns do not
 *     exist at all — an empty column would already say "there is a figure you may not see".
 *
 * Every duration is minutes / the person's conversion rate, so a person without a rate shows
 * "—" instead of a guessed 8:24 day (trap 13). The footer sums the converted days over the rows
 * that have a rate AND daily rows, and the rates over the summed minutes of the rows with daily
 * rows — exactly the server summary's rule, so the tiles and the footer agree with no filter on.
 *
 * A person with no daily-time row in the period is MARKED, not filtered (plan P3: Ferrat,
 * Muller, Gambone): the row is dimmed, its cells still show its own figures, and the rates read
 * "—" because they are undefined, not 0. Only the footer leaves such a row out.
 */

type FixedKey = 'name' | 'companyCode' | 'orgUnit' | 'targetDays' | 'absenceRate' | 'sickRate' | 'data';
type CoarseKey = `coarse:${AbsenceCoarseGroup}`;
type FineKey = `fine:${AbsenceFineGroup}`;
type ColKey = FixedKey | CoarseKey | FineKey;

interface ColDef {
    key: ColKey;
    label: string;
    width: number;
    align?: 'right';
    coarse?: AbsenceCoarseGroup;
    fine?: AbsenceFineGroup;
}

const FIXED_LEFT: ColDef[] = [
    { key: 'name', label: 'Name', width: 200 },
    { key: 'companyCode', label: 'Company', width: 80 },
    { key: 'orgUnit', label: 'Org unit', width: 150 },
    { key: 'targetDays', label: 'Target days', width: 95, align: 'right' },
];

/** The tenant's groups — header words are theirs, not translated. */
const COARSE_COLUMNS: ColDef[] = [
    { key: 'coarse:FEIERTAG', label: 'Feiertag', width: 90, align: 'right', coarse: 'FEIERTAG' },
    { key: 'coarse:FERIEN', label: 'Ferien', width: 90, align: 'right', coarse: 'FERIEN' },
    { key: 'coarse:ABSENZ', label: 'Absenz', width: 90, align: 'right', coarse: 'ABSENZ' },
    { key: 'coarse:AUSWAERTS', label: 'Auswärts', width: 90, align: 'right', coarse: 'AUSWAERTS' },
];

/** A type HR has not grouped yet: shown as its own column ONLY when something landed there, never dropped. */
const UNCLASSIFIED_COLUMN: ColDef = { key: 'coarse:UNCLASSIFIED', label: 'Unclassified', width: 100, align: 'right', coarse: 'UNCLASSIFIED' };

/** Fine groups that are not already a coarse bucket of their own (FEIERTAG, FERIEN, AUSWAERTS are). */
const FINE_COLUMNS: ColDef[] = [
    { key: 'fine:KRANKHEIT', label: 'Sickness', width: 90, align: 'right', fine: 'KRANKHEIT' },
    { key: 'fine:UNFALL', label: 'Accident', width: 90, align: 'right', fine: 'UNFALL' },
    { key: 'fine:GESUNDHEIT_ANDERE', label: 'Other health', width: 110, align: 'right', fine: 'GESUNDHEIT_ANDERE' },
    { key: 'fine:KOMPENSATION', label: 'Compensation', width: 110, align: 'right', fine: 'KOMPENSATION' },
    { key: 'fine:ABSENZ_ANDERE', label: 'Other absence', width: 110, align: 'right', fine: 'ABSENZ_ANDERE' },
];

const ABSENCE_RATE_COLUMN: ColDef = { key: 'absenceRate', label: 'Absence rate', width: 100, align: 'right' };
const SICK_RATE_COLUMN: ColDef = { key: 'sickRate', label: 'Sick rate', width: 90, align: 'right' };
const DATA_COLUMN: ColDef = { key: 'data', label: 'Data', width: 90 };

/**
 * EVERY column the table can show, in display order — what the width store is keyed on. The
 * resize hook drops stored widths of keys it does not know, so it must always see the full set:
 * a supervisor's session (no fine columns) would otherwise wipe the widths HR dragged for them.
 */
const ALL_COLUMNS: ColDef[] = [
    ...FIXED_LEFT, ...COARSE_COLUMNS, UNCLASSIFIED_COLUMN, ...FINE_COLUMNS, ABSENCE_RATE_COLUMN, SICK_RATE_COLUMN, DATA_COLUMN,
];

const FILTER_LABELS = { search: 'Search…', all: 'All', none: 'None', from: 'from', to: 'to', empty: '(blank)' };

/** Rates filter and sort in percent points, so "min 5" in the filter means 5 %. */
const pct = (ratio: number | null): number | null => (ratio === null ? null : Math.round(ratio * 1000) / 10);

const FIXED_ACCESSOR: Record<FixedKey, (r: AbsenceRow) => string | number | null> = {
    name: r => r.name,
    companyCode: r => r.companyCode?.toUpperCase() ?? null,
    orgUnit: r => r.orgUnit,
    targetDays: r => r.targetDays,
    absenceRate: r => pct(r.absenceRate),
    sickRate: r => pct(r.sickRate),
    data: r => (r.noDailyRows ? 'no data' : 'ok'),
};

interface Props {
    rows: AbsenceRow[];
    summary: AbsenceSummary;
    typesVisible: boolean;
    viewer: HoursViewer;
    loading: boolean;
    /** Scope-specific text for "no rows at all" — the caller knows why the list is empty. */
    emptyMessage: string;
    onOpenDays: (row: AbsenceRow) => void;
}

export function AbsencesTable({ rows, summary, typesVisible, viewer, loading, emptyMessage, onOpenDays }: Props) {
    const [sort, setSort] = useState<SortState<ColKey>>({ key: 'name', direction: 'asc' });

    const hasUnclassified = useMemo(() => rows.some(r => r.coarseMin.UNCLASSIFIED > 0), [rows]);

    const columns = useMemo<ColDef[]>(() => [
        ...FIXED_LEFT,
        ...COARSE_COLUMNS,
        ...(hasUnclassified ? [UNCLASSIFIED_COLUMN] : []),
        ...(typesVisible ? FINE_COLUMNS : []),
        ABSENCE_RATE_COLUMN,
        ...(typesVisible ? [SICK_RATE_COLUMN] : []),
        DATA_COLUMN,
    ], [hasUnclassified, typesVisible]);

    const accessor = useMemo(() => {
        const byKey = new Map<ColKey, (r: AbsenceRow) => string | number | null>();
        for (const c of columns) {
            if (c.coarse) {
                const g = c.coarse;
                byKey.set(c.key, r => minutesAsDays(r.coarseMin[g], r.conversionRate));
            } else if (c.fine) {
                const g = c.fine;
                byKey.set(c.key, r => (r.fineMin === null ? null : minutesAsDays(r.fineMin[g], r.conversionRate)));
            } else {
                byKey.set(c.key, FIXED_ACCESSOR[c.key as FixedKey]);
            }
        }
        return (key: ColKey) => byKey.get(key) ?? (() => null);
    }, [columns]);

    const isNumeric = (key: ColKey) => key.startsWith('coarse:') || key.startsWith('fine:') || key === 'targetDays' || key === 'absenceRate' || key === 'sickRate';

    const filterColumns = useMemo(
        () => columns.map(c => ({ key: c.key, accessor: accessor(c.key), numeric: isNumeric(c.key) })),
        [columns, accessor],
    );

    // The full set for the widths (see ALL_COLUMNS); `columns` is the visible subset rendered below.
    const { widthOf, resizingKey, startResize, resetWidth, shouldSuppressClick } =
        useResizableColumns(ALL_COLUMNS, 'timerocket-absences-col-widths');
    const { filtered, filters, setColumnFilter, isFiltered, activeCount, clearAll, uniqueValuesFor } =
        useColumnFilters(rows, filterColumns);

    // A drag ends with a mouseup inside the header cell, which the browser reports as a click.
    // Without this guard every resize would also re-sort the table.
    const onSort = (key: ColKey) => {
        if (shouldSuppressClick()) return;
        setSort(s => nextSort(s, key, isNumeric(key)));
    };

    // Sorting and totals happen over the FILTERED rows, so the footer never describes
    // something other than what is on screen.
    const sorted = sort.key
        ? [...filtered].sort((a, b) => {
            const acc = accessor(sort.key as ColKey);
            const av = acc(a);
            const bv = acc(b);
            if (av === null || av === undefined || av === '') return 1;
            if (bv === null || bv === undefined || bv === '') return -1;
            const base = typeof av === 'number' && typeof bv === 'number'
                ? av - bv
                : String(av).localeCompare(String(bv), undefined, { numeric: true });
            return base * (sort.direction === 'asc' ? 1 : -1);
        })
        : filtered;

    // The footer's ONE rule, shared with the server's summary: a row without daily rows has no
    // target, hence no denominator — and its minutes must not inflate the numerator either. So
    // every footer sum skips those rows: minutes, days and target alike.
    /** Sum of a converted-days column over the rows that have a rate and daily rows; null when none has. */
    const daysSum = (key: ColKey): number | null => {
        const acc = accessor(key);
        return filtered.reduce<number | null>((s, r) => {
            if (r.noDailyRows) return s;
            const v = acc(r);
            return typeof v === 'number' ? (s ?? 0) + v : s;
        }, null);
    };

    const sums = filtered.reduce(
        (s, r) => ({
            targetDays: s.targetDays + (r.noDailyRows ? 0 : r.targetDays),
            targetMin: s.targetMin + (r.noDailyRows ? 0 : r.targetMin),
            absenceMin: s.absenceMin + (r.noDailyRows ? 0 : r.absenceMin),
            sickMin: s.sickMin + (r.noDailyRows ? 0 : (r.sickMin ?? 0)),
        }),
        { targetDays: 0, targetMin: 0, absenceMin: 0, sickMin: 0 },
    );
    const footerAbsenceRate = sums.targetMin > 0 ? sums.absenceMin / sums.targetMin : null;
    const footerSickRate = typesVisible && sums.targetMin > 0 ? sums.sickMin / sums.targetMin : null;

    // An HR-only row (no TimeRocket record, P4) has no daily detail to open.
    const canOpen = (row: AbsenceRow) => row.employeeId !== null && (viewer.canSeeAll || (viewer.employeeId !== null && row.employeeId === viewer.employeeId));

    const dayCell = (key: string, min: number, rate: number | null, dim: boolean) => (
        <td key={key} className={`px-3 py-1.5 text-right tabular-nums ${dim || min === 0 ? 'text-gray-400' : ''}`}
            title={rate === null ? 'no conversion rate' : undefined}>
            {formatMinutesAsDays(min, rate)}
        </td>
    );

    return (
        <div className="border rounded-lg bg-white overflow-hidden">
            {activeCount > 0 && (
                <div className="flex items-center justify-between px-4 py-1.5 border-b bg-amber-50 text-xs text-amber-900">
                    <span>{activeCount} filter(s) active</span>
                    <button onClick={clearAll} className="text-blue-600 hover:text-blue-800 font-medium">Clear all</button>
                </div>
            )}

            {/* The sticky header needs a bounded scroll container or it does not stick. The floor is
                for the one-row own view: a filter popover (~300 px) opened over a 100 px table would
                be clipped by this very container, and the own view ALWAYS has one row. */}
            <div className="overflow-auto" style={{ maxHeight: '70vh', minHeight: rows.length > 0 && rows.length <= 3 ? 360 : undefined }}>
                {loading && rows.length === 0 && (
                    <div className="p-12 text-center text-gray-500">
                        <Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading…
                    </div>
                )}
                {!loading && rows.length === 0 && (
                    <div className="p-8 text-center text-gray-500">{emptyMessage}</div>
                )}
                {rows.length > 0 && filtered.length === 0 && (
                    <div className="p-8 text-center text-gray-500">{activeCount} filter(s) active — no row matches.</div>
                )}
                {rows.length > 0 && filtered.length > 0 && (
                    <table className={`w-full text-sm ${loading ? 'opacity-60' : ''}`} style={{ tableLayout: 'fixed' }}>
                        <colgroup>
                            {columns.map(c => <col key={c.key} style={{ width: widthOf(c.key) }} />)}
                        </colgroup>
                        <thead>
                            <tr className="border-b">
                                {columns.map(c => (
                                    <SortableHeader
                                        key={c.key}
                                        label={c.label}
                                        sortKey={c.key}
                                        sort={sort}
                                        onSort={onSort}
                                        align={c.align}
                                        onResizeStart={x => startResize(c.key, x)}
                                        onResetWidth={() => resetWidth(c.key)}
                                        isResizing={resizingKey === c.key}
                                        filter={
                                            <ColumnFilter
                                                label={c.label}
                                                values={uniqueValuesFor(c.key)}
                                                state={filters[c.key]}
                                                onChange={s => setColumnFilter(c.key, s)}
                                                numeric={isNumeric(c.key)}
                                                active={isFiltered(c.key)}
                                                labels={FILTER_LABELS}
                                            />
                                        }
                                    />
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {sorted.map(r => {
                                const openable = canOpen(r);
                                const dim = r.noDailyRows;
                                return (
                                    <tr
                                        key={r.employeeId ?? `hr-${r.staffId}`}
                                        onClick={openable ? () => onOpenDays(r) : undefined}
                                        title={dim ? 'no daily rows in this period' : openable ? 'Open the daily detail' : undefined}
                                        className={`${openable ? 'cursor-pointer hover:bg-blue-50' : 'hover:bg-gray-50'} ${dim ? 'text-gray-400' : ''}`}
                                    >
                                        {/* The title names the contract the day conversion comes from — a later
                                            workload change must not re-price this period's days, and the date
                                            lets HR see which contract did. */}
                                        <td
                                            className="px-3 py-1.5 font-medium truncate"
                                            title={r.contractValidFrom ? `${r.name} — rate from contract valid from ${formatDateLong(r.contractValidFrom)}` : r.name}
                                        >
                                            {r.name}
                                            {!r.isActive && <span className="ml-1 text-xs text-gray-400">(left)</span>}
                                            <DiscrepancyBadges items={r.discrepancies} />
                                        </td>
                                        <td className={`px-3 py-1.5 ${dim ? '' : 'text-gray-600'}`}>{r.companyCode?.toUpperCase() ?? '—'}</td>
                                        <td className={`px-3 py-1.5 truncate ${dim ? '' : 'text-gray-600'}`} title={r.orgUnit ?? undefined}>{r.orgUnit ?? '—'}</td>
                                        <td className="px-3 py-1.5 text-right tabular-nums">{r.targetDays}</td>
                                        {COARSE_COLUMNS.map(c => dayCell(c.key, r.coarseMin[c.coarse!], r.conversionRate, dim))}
                                        {hasUnclassified && dayCell(UNCLASSIFIED_COLUMN.key, r.coarseMin.UNCLASSIFIED, r.conversionRate, dim)}
                                        {typesVisible && r.fineMin !== null && FINE_COLUMNS.map(c => dayCell(c.key, r.fineMin![c.fine!], r.conversionRate, dim))}
                                        {/* typesVisible but this row has no fine data — cannot happen by contract, but the column
                                            count must stay stable for the colgroup, so pad rather than shift the cells. */}
                                        {typesVisible && r.fineMin === null && FINE_COLUMNS.map(c => <td key={c.key} className="px-3 py-1.5 text-right text-gray-400">—</td>)}
                                        <td className="px-3 py-1.5 text-right tabular-nums" title={r.absenceRate === null ? 'undefined — no target in this period' : undefined}>
                                            {formatPercent(r.absenceRate)}
                                        </td>
                                        {typesVisible && (
                                            <td className="px-3 py-1.5 text-right tabular-nums" title={r.sickRate === null ? 'undefined — no target in this period' : undefined}>
                                                {formatPercent(r.sickRate)}
                                            </td>
                                        )}
                                        <td className="px-3 py-1.5">
                                            {r.noDailyRows && (
                                                <span className="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600" title="no daily rows in this period">
                                                    no data
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot className="bg-gray-50 border-t font-medium">
                            <tr>
                                <td className="px-3 py-2" colSpan={3}>
                                    {activeCount > 0
                                        ? `${filtered.length} of ${rows.length} people`
                                        : `${summary.people} people`}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums">{sums.targetDays}</td>
                                {COARSE_COLUMNS.map(c => (
                                    <td key={c.key} className="px-3 py-2 text-right tabular-nums" title="Days over the rows with a conversion rate and daily rows">
                                        {formatDays(daysSum(c.key))}
                                    </td>
                                ))}
                                {hasUnclassified && (
                                    <td className="px-3 py-2 text-right tabular-nums" title="Days over the rows with a conversion rate and daily rows">
                                        {formatDays(daysSum(UNCLASSIFIED_COLUMN.key))}
                                    </td>
                                )}
                                {typesVisible && FINE_COLUMNS.map(c => (
                                    <td key={c.key} className="px-3 py-2 text-right tabular-nums" title="Days over the rows with a conversion rate and daily rows">
                                        {formatDays(daysSum(c.key))}
                                    </td>
                                ))}
                                <td className="px-3 py-2 text-right tabular-nums" title="Absence minutes over target minutes of the rows shown that have daily rows">
                                    {formatPercent(footerAbsenceRate)}
                                </td>
                                {typesVisible && (
                                    <td className="px-3 py-2 text-right tabular-nums" title="Sickness + accident minutes over target minutes of the rows shown that have daily rows">
                                        {formatPercent(footerSickRate)}
                                    </td>
                                )}
                                <td />
                            </tr>
                        </tfoot>
                    </table>
                )}
            </div>
        </div>
    );
}
